import request from "../utils/request";

/**
 * 文档分类（Doc Category）与文档标签（Doc Tag）的服务层。
 *
 * 两者的接口形状完全一致，仅资源名与字段前缀不同，故由同一个工厂生成。
 * 对页面暴露的类型刻意沿用后端接入前的字段命名（snake_case），
 * 这样页面组件无需改动渲染逻辑，只换数据来源。
 */

// ─── 类型定义 ─────────────────────────────────────────────────────────────────

export interface DocTaxonomyItem {
  id: string;
  name: string;
  prompt: string | null;
  stable_key: string;
  is_builtin: boolean;
  is_editable: boolean;
  is_deletable: boolean;
  /** 仅 Document Category 使用；Doc Tag 响应中缺省为 false */
  is_visible_to_all_users?: boolean;
  /** 仅 Document Category 使用；Doc Tag 响应中缺省为 false */
  is_default_selected?: boolean;
  created_at: string;
}

export interface DocCategoryAccessSettings {
  isVisibleToAllUsers: boolean;
  isDefaultSelected: boolean;
}

/** WTM ListVM 返回的原始行：带实体名前缀的字段 + 基类同名字段 */
interface DocTaxonomyItemWire {
  ID?: string;
  Name?: string;
  Prompt?: string | null;
  IsSystem?: boolean;
  CreateTime?: string;
  [key: string]: unknown;
}

interface SearchResponseWire {
  Count?: number;
  Data?: DocTaxonomyItemWire[];
}

// ─── 工厂 ────────────────────────────────────────────────────────────────────

function createDocTaxonomyApi(
  resource: "doccategory" | "doctag",
  fieldPrefix: string,
  stableKeyField: "Code" | "TagKey",
) {
  const base = `/project/${resource}`;
  const pick = <T>(row: DocTaxonomyItemWire, field: string, fallback: T): T =>
    (row[`${fieldPrefix}_${field}`] as T) ?? (row[field] as T) ?? fallback;

  function normalize(row: DocTaxonomyItemWire): DocTaxonomyItem {
    // 后端只存 IsSystem 一个标记，可编辑 / 可删除都是它的派生结果。
    const isSystem = Boolean(pick(row, "IsSystem", false));
    return {
      id: String(row.ID ?? ""),
      name: pick(row, "Name", ""),
      prompt: pick<string | null>(row, "Prompt", null) || null,
      stable_key: pick(row, stableKeyField, ""),
      is_builtin: isSystem,
      is_editable: !isSystem,
      is_deletable: !isSystem,
      is_visible_to_all_users: Boolean(pick(row, "IsVisibleToAllUsers", false)),
      is_default_selected: Boolean(pick(row, "IsDefaultSelected", false)),
      created_at: pick(row, "CreateTime", ""),
    };
  }

  return {
    /**
     * 拉取全量列表。后端已按「Custom 在前（创建时间倒序）→ System 在后（固定序）」排好，
     * 前端直接按顺序渲染即可；搜索仍在前端本地做（数据量个位数）。
     */
    list(): Promise<DocTaxonomyItem[]> {
      return request
        .post<SearchResponseWire>(`${base}/search${resource}`, {})
        .then((res) => (res.data?.Data ?? []).map(normalize));
    },

    create(name: string, prompt: string | null, settings?: DocCategoryAccessSettings) {
      return request.post(`${base}/create`, {
        Entity: {
          Name: name,
          Prompt: prompt,
          ...(resource === "doccategory" && settings
            ? {
                IsVisibleToAllUsers: settings.isVisibleToAllUsers,
                IsDefaultSelected: settings.isDefaultSelected,
              }
            : {}),
        },
      });
    },

    update(id: string, name: string, prompt: string | null, settings?: DocCategoryAccessSettings) {
      return request.put(`${base}/edit`, {
        Entity: {
          ID: id,
          Name: name,
          Prompt: prompt,
          ...(resource === "doccategory" && settings
            ? {
                IsVisibleToAllUsers: settings.isVisibleToAllUsers,
                IsDefaultSelected: settings.isDefaultSelected,
              }
            : {}),
        },
      });
    },

    remove(id: string) {
      return request.post<number>(`${base}/batchdelete`, [id]);
    },
  };
}

// ─── 导出 ────────────────────────────────────────────────────────────────────

const docCategoryCrudApi = createDocTaxonomyApi("doccategory", "DocCategory", "Code");

export const docCategoryApi = {
  ...docCategoryCrudApi,

  setVisibleToAllUsers(id: string, enabled: boolean) {
    return request.put(`/project/doccategory/${id}/visible-to-all-users`, { Enabled: enabled });
  },

  setDefaultSelected(id: string, enabled: boolean) {
    return request.put(`/project/doccategory/${id}/default-selected`, { Enabled: enabled });
  },
};
export const docTagApi = createDocTaxonomyApi("doctag", "DocTag", "TagKey");

/**
 * 把接口错误转成可展示的文案。
 * WTM 校验失败返回 { Name: ["..."] } 或 { code, message }，都收敛到一句话。
 */
export function toErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;

  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.message === "string" && obj.message) return obj.message;
    // WTM ModelState 错误：取第一条
    for (const value of Object.values(obj)) {
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
      if (typeof value === "string" && value) return value;
    }
  }

  return err instanceof Error && err.message ? err.message : fallback;
}
